import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LOGO, NAV_LINKS, PHONE, PHONE_HREF } from "../lib/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ink/90 backdrop-blur-xl border-b border-gold/20 py-2"
          : "bg-transparent border-b border-transparent py-3"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src={LOGO}
            alt="MB Notary Services"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40 transition-transform duration-500 group-hover:rotate-6"
          />
          <div className="leading-tight">
            <div className="font-serif text-gold text-lg tracking-wide">MB Notary</div>
            <div className="eyebrow text-taupe text-[9px]">Services LLC</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="link-underline text-ivory/85 hover:text-gold text-sm font-light tracking-wide transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a href={PHONE_HREF} className="flex items-center gap-2 text-ivory/80 hover:text-gold text-sm transition-colors" data-testid="nav-phone">
            <Phone size={15} strokeWidth={1.5} className="text-gold" /> {PHONE}
          </a>
          <a href="#booking" className="btn-gold rounded-full px-6 py-2.5 text-xs font-medium tracking-widest uppercase" data-testid="nav-book-btn">
            Book Appointment
          </a>
        </div>

        <button
          className="lg:hidden text-gold p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-t border-gold/20"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-ivory/85 hover:text-gold text-base tracking-wide"
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
              <a href="#booking" onClick={() => setOpen(false)} className="btn-gold rounded-full px-6 py-3 text-xs font-medium tracking-widest uppercase text-center mt-2" data-testid="mobile-book-btn">
                Book Appointment
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
